import { API } from '@/lib/api';
import { useState, useEffect } from "react";
import { getErrorMessage } from "@/lib/utils";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ArrowLeft, Save, LocateFixed, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import axios from "axios";



const AddressManagementPage = () => {
  const [address, setAddress] = useState({ street: "", city: "", province: "", postal_code: "", country: "Canada" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);

  useEffect(() => {
    const fetchAddress = async () => {
      try {
        const res = await axios.get(`${API}/user/address`, { withCredentials: true });
        if (res.data.address) setAddress(res.data.address);
      } catch { /* no address yet */ }
      setLoading(false);
    };
    fetchAddress();
  }, []);

  const handleSave = async () => {
    if (!address.street || !address.city || !address.province || !address.postal_code) {
      toast.error("Please fill in all required fields"); return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/user/address`, address, { withCredentials: true });
      toast.success("Address updated successfully");
    } catch (err) { toast.error(getErrorMessage(err, "Update failed")); }
    setSaving(false);
  };

  const handleGPS = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json`);
          const data = await res.json();
          const addr = data.address || {};
          setAddress({
            street: [addr.house_number, addr.road].filter(Boolean).join(" ") || "",
            city: addr.city || addr.town || addr.village || "",
            province: addr.state || "",
            postal_code: addr.postcode || "",
            country: addr.country || "Canada",
          });
          toast.success("Location detected");
        } catch { toast.error("Could not resolve address"); }
        setLocating(false);
      },
      () => { toast.error("Location access denied"); setLocating(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const update = (field, value) => setAddress((prev) => ({ ...prev, [field]: value }));

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50"><Navbar />
        <div className="flex items-center justify-center pt-40"><MapPin className="w-8 h-8 animate-pulse text-emerald-600" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="address-management-page">
      <Navbar />
      <div className="pt-24 pb-16 px-4 max-w-3xl mx-auto">
        <Link to="/professional/dashboard" className="inline-flex items-center gap-2 text-stone-500 hover:text-emerald-950 mb-6 transition-colors text-sm" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> Back to Work Hub
        </Link>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-emerald-950 flex items-center gap-2">
              <MapPin className="w-6 h-6 text-emerald-600" /> Address Management
            </h1>
            <Button variant="outline" onClick={handleGPS} disabled={locating} className="rounded-full" data-testid="gps-btn">
              <LocateFixed className={`w-4 h-4 mr-1.5 ${locating ? "animate-spin" : ""}`} />
              {locating ? "Detecting..." : "Use GPS"}
            </Button>
          </div>

          <div className="bg-white rounded-2xl border border-stone-100 p-6" data-testid="address-form">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center"><Home className="w-5 h-5 text-emerald-600" /></div>
              <div>
                <h3 className="font-semibold text-emerald-950">Home / Mailing Address</h3>
                <p className="text-xs text-stone-500">Used for payouts and official correspondence</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Street Address *</Label>
                <Input value={address.street} onChange={(e) => update("street", e.target.value)} placeholder="123 Main Street, Unit 4" data-testid="street-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">City *</Label>
                  <Input value={address.city} onChange={(e) => update("city", e.target.value)} placeholder="Toronto" data-testid="city-input" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Province *</Label>
                  <Input value={address.province} onChange={(e) => update("province", e.target.value)} placeholder="Ontario" data-testid="province-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Postal Code *</Label>
                  <Input value={address.postal_code} onChange={(e) => update("postal_code", e.target.value)} placeholder="M5V 2T6" data-testid="postal-input" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Country</Label>
                  <Input value={address.country} onChange={(e) => update("country", e.target.value)} placeholder="Canada" data-testid="country-input" />
                </div>
              </div>
              <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white w-full mt-2" data-testid="save-address-btn">
                <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving..." : "Save Address"}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AddressManagementPage;
